// ============================================================================
// /clock — the WORKER'S own-phone punch page (TCSD-0140). PUBLIC, no account.
//
// Identity: mobile number + the PIN HR handed over. The first punch binds this
// phone at reduced trust and HR confirms the pairing at /time/admin, which
// still feeds the daily review. Punching NEVER waits for anything (D-03) — the
// only refusal is a wrong phone+PIN, and it says so in one calm sentence with
// no hint of which half was wrong.
//
// The ruling's persistence ("session persists on the bound device, no
// re-verification in normal operation") is implemented as localStorage of the
// phone + PIN after a successful use. Accepted trade-off, same shape as
// TCSD-0140's own: the model detects rather than prevents device sharing.
//
// Bilingual ES/EN, Spanish first — the workforce's preferred language is
// overwhelmingly Spanish. Strings live in one table below.
//
// ⛔ THE LANGUAGE IS A FACT ABOUT THE PERSON (2026-09-22). The switch writes
// through to the worker's own record when they are signed in, so a shared
// kiosk, a cleared browser or a second phone all open in the language they
// chose. localStorage is the fallback for BEFORE sign-in only, when there is
// nobody to attach the choice to. Spanish is still what somebody with no
// preference recorded gets.
//
// ⛔ THE HOURS CARD NO LONGER WAITS FOR HR (Ben, 2026-09-22, reversing the
// hours half of TCSD-0140 on sight as that ruling allowed). The same mobile
// number and PIN that authorize a punch — which CREATES a shift — now also
// read one. Everything else in TCSD-0140 stands.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Button } from '../../shared/components';
import { errorMessage } from '../../shared/errors';
import { workerClock } from './hooks/use-time-data';
import { formatSiteDateTime, formatSiteTime, isoToSiteInput, siteInputToISO, tzLabel } from '../../lib/siteTime';
import type {
  LocationState,
  PendingAutoclose,
  PunchResult,
  TonightSummary,
  VisitOption,
  WhereAmI,
  WhereOption,
  WorkerContext,
  WorkerHours,
  WorkerPeriod,
} from './types';

/** HR's help line (Google Voice). TCSD-0140 says print it on every worker
 *  surface; the number itself has not been provided yet — when it is, set it
 *  here and it appears in the footer and on every refusal. */
export const HR_HELP_LINE: string | null = null;

export type Lang = 'es' | 'en';

/** Shared with the building time clock (/clock/kiosk), which adds only its own lines. */
export const STR: Record<Lang, Record<string, string>> = {
  es: {
    title: 'Reloj de Terminal Cleaning Services',
    phone: 'Número de celular',
    pin: 'PIN (lo entrega Recursos Humanos)',
    signIn: 'Entrar',
    hello: 'Hola',
    notYou: '¿No eres tú? Cambiar',
    onClockSince: 'En turno desde',
    at: 'en',
    where: '¿Dónde estás trabajando?',
    locating: 'Buscando dónde estás…',
    youAreAt: 'Estás en',
    whichOne: '¿En cuál de estos estás?',
    notHere: 'No estoy en ninguno de estos',
    noPlace: 'Ubicación no disponible',
    noPlaceBody: 'No importa — marca de todos modos. Recursos Humanos anota el edificio después.',
    noPlaceChosen: 'Sin edificio',
    whereAreYou: '¿Dónde estás?',
    scopeAll: 'Todavía no tenemos registrados tus edificios, así que aquí están todos.',
    findBuilding: 'Escribe el nombre del edificio',
    notNearAny: 'No estás cerca de un edificio',
    itShowsYou: 'Te muestra a',
    fromA: 'de',
    punchAnyway: '¿Quieres marcar de todos modos?',
    firstTimeTitle: 'Tu teléfono y tu PIN funcionan.',
    firstTimeBody: 'No se registró nada — esto fue sólo una prueba. Marca tu entrada cuando de verdad empieces.',
    firstTimeOk: 'Entendido',
    punchIn: 'MARCAR ENTRADA',
    punchOut: 'MARCAR SALIDA',
    recorded: 'Marcada registrada',
    recordedAlways: 'Tu marcada SIEMPRE se registra — un problema de GPS o de teléfono nunca te quita horas.',
    myHours: 'Mis horas',
    refresh: 'Actualizar',
    thisPeriod: 'Este período',
    lastPeriod: 'Período anterior',
    total: 'Total',
    noHours: 'Sin horas en este período.',
    estimated: 'Estimado',
    perHour: '/ hora',
    colDay: 'Día',
    colIn: 'Entrada',
    colOut: 'Salida',
    colHours: 'Horas',
    stillOpen: 'en turno',
    noRateYet: 'Todavía no tenemos tu tarifa registrada. Aquí están tus horas; Recursos Humanos confirma la tarifa antes de la nómina.',
    someHoursNoRate: 'Algunas horas todavía no tienen tarifa confirmada, así que no están incluidas en el estimado.',
    estimateNote: 'Estimado, no es un cheque. Sólo tus horas — nada de nadie más.',
    needsAnswer: 'Esta noche necesita tu respuesta. Contesta en tu próxima marcada, o avísale a tu gerente.',
    promptTitle: 'Antes de seguir — un turno necesita tu respuesta',
    promptBody1: 'Marcaste entrada',
    promptBody2: 'y no marcaste salida. El sistema cerró el turno automáticamente',
    forgotOut: 'Olvidé marcar salida',
    workedFull: 'Trabajé todo ese tiempo',
    notSure: 'No estoy seguro/a',
    later: 'Responder después',
    whenLeave: '¿A qué hora saliste de verdad?',
    saveOut: 'Guardar salida',
    noteOptional: '¿Algo que Recursos Humanos deba saber? (opcional)',
    noteRequired: 'Cuéntanos qué pasó — una frase basta.',
    sendHr: 'Enviar a Recursos Humanos',
    sentHrFull: 'Gracias — Recursos Humanos confirmará las horas completas antes de la nómina.',
    sentHrNotSure: 'Gracias — Recursos Humanos revisará ese turno.',
    savedOut: 'Gracias — turno corregido. Listo.',
    deferred: 'Está bien — te lo preguntaremos de nuevo más tarde.',
    help: '¿Preguntas? Llama o manda mensaje a Recursos Humanos',
    helpNoNumber: '¿Preguntas? Pregunta a tu supervisor por la línea de Recursos Humanos.',
    working: 'Un momento…',
    hours: 'h',
    // Night managers (item #997)
    nmPunchInHint: 'Marca tu entrada de la noche. No se pide edificio.',
    nmPunchOut: 'MARCAR SALIDA (fin de la noche)',
    checkInBuilding: 'Marcar llegada a un edificio',
    whichBuilding: '¿En qué edificio estás?',
    closest: 'Más cercano',
    yourMarket: 'Edificios de tu mercado',
    inBuilding: 'En',
    checkOutBuilding: 'Salir del edificio',
    checkInElsewhere: 'Llegar a otro edificio',
    elsewhereNote: 'Llegar a otro edificio, o marcar tu salida, cierra este automáticamente.',
    cancel: 'Cancelar',
    checkedOut: 'Saliste del edificio.',
    tonightTitle: 'Esta noche — lo que verá tu gerente',
    driving: 'Manejando / entre edificios',
    hoursWorked: 'Horas trabajadas',
    noCheckins: 'No marcaste llegada a ningún edificio esta noche.',
    tonightNote: 'Sólo lectura. Sólo esta noche — desaparece en tu próxima entrada.',
  },
  en: {
    title: 'Terminal Cleaning Services time clock',
    phone: 'Mobile number',
    pin: 'PIN (HR hands it to you)',
    signIn: 'Continue',
    hello: 'Hi',
    notYou: 'Not you? Switch',
    onClockSince: 'On the clock since',
    at: 'at',
    where: 'Where are you working?',
    locating: 'Finding where you are…',
    youAreAt: 'You are at',
    whichOne: 'Which one are you in?',
    notHere: 'I am not at any of these',
    noPlace: 'Location unavailable',
    noPlaceBody: 'That is fine — punch anyway. HR records the building afterwards.',
    noPlaceChosen: 'No building',
    whereAreYou: 'Where are you?',
    scopeAll: 'We do not have your buildings recorded yet, so here are all of them.',
    findBuilding: 'Type the building name',
    notNearAny: 'You are not near a building',
    itShowsYou: 'It shows you',
    fromA: 'from',
    punchAnyway: 'Do you still want to punch in?',
    firstTimeTitle: 'Your phone and PIN work.',
    firstTimeBody: 'Nothing was recorded — this was just a check. Punch in when you actually start.',
    firstTimeOk: 'Got it',
    punchIn: 'PUNCH IN',
    punchOut: 'PUNCH OUT',
    recorded: 'Punch recorded',
    recordedAlways: 'Your punch ALWAYS records — a GPS or phone problem never costs you hours.',
    myHours: 'My hours',
    refresh: 'Refresh',
    thisPeriod: 'This period',
    lastPeriod: 'Last period',
    total: 'Total',
    noHours: 'No hours in this period.',
    estimated: 'Estimated',
    perHour: '/ hour',
    colDay: 'Day',
    colIn: 'In',
    colOut: 'Out',
    colHours: 'Hours',
    stillOpen: 'on the clock',
    noRateYet: 'We do not have your rate on file yet. Here are your hours; HR confirms the rate before payroll.',
    someHoursNoRate: 'Some hours do not have a confirmed rate yet, so they are not in the estimate.',
    estimateNote: 'An estimate, not a cheque. Your hours only — nobody else’s.',
    needsAnswer: 'This night needs your answer. Answer it at your next punch, or tell your manager.',
    promptTitle: 'Before you go — a shift needs your answer',
    promptBody1: 'You punched in',
    promptBody2: 'and never punched out. The system closed the shift automatically',
    forgotOut: 'I forgot to punch out',
    workedFull: 'I worked that whole time',
    notSure: "I'm not sure",
    later: 'Answer later',
    whenLeave: 'When did you actually leave?',
    saveOut: 'Save punch-out',
    noteOptional: 'Anything HR should know? (optional)',
    noteRequired: 'Tell us what happened — a sentence is enough.',
    sendHr: 'Send to HR',
    sentHrFull: 'Thanks — HR will confirm the full hours before payroll.',
    sentHrNotSure: 'Thanks — HR will look at that shift.',
    savedOut: 'Thanks — shift corrected. Done.',
    deferred: 'Okay — we will ask again later.',
    help: 'Questions? Call or text HR',
    helpNoNumber: 'Questions? Ask your supervisor for the HR line.',
    working: 'One moment…',
    hours: 'h',
    // Night managers (item #997)
    nmPunchInHint: 'Punch in for the night. No building asked.',
    nmPunchOut: 'PUNCH OUT (end of night)',
    checkInBuilding: 'Check in at a building',
    whichBuilding: 'Which building are you at?',
    closest: 'Closest',
    yourMarket: 'Buildings in your market',
    inBuilding: 'In',
    checkOutBuilding: 'Check out of building',
    checkInElsewhere: 'Check in somewhere else',
    elsewhereNote: 'Checking in somewhere else, or punching out, closes this one automatically.',
    cancel: 'Cancel',
    checkedOut: 'Checked out of the building.',
    tonightTitle: 'Tonight — what your AM will see',
    driving: 'Driving / between buildings',
    hoursWorked: 'Hours worked',
    noCheckins: 'You did not check in at any building tonight.',
    tonightNote: 'Read-only. This night only — gone at your next punch-in.',
  },
};

const LS_PHONE = 'tcs-clock-phone';
const LS_PIN = 'tcs-clock-pin';
const LS_LANG = 'tcs-clock-lang';

// ⛔ EVERY CLOCK TIME ON THIS PAGE IS ON THE WORKER'S CLOCK — the building's
// market — with its label, in both languages ("7:52 PM CT"). Not the phone's:
// a phone set to the wrong zone used to show the wrong time, and a staff screen
// in Spain showed another (Ben, 2026-09-24). Nothing else on this page changed.
function fmtWhenLocal(iso: string, tz: string | null): string {
  return formatSiteDateTime(iso, tz);
}

function fmtDate(d: string): string {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** How far away, in words somebody standing in a car park can use. */
function fmtAway(metres: number): string {
  if (metres < 1000) return `${Math.round(metres)} m`;
  return `${(metres / 1000).toFixed(metres < 10000 ? 1 : 0)} km`;
}

/** A clock time on the worker's own card, on the building's clock. */
function fmtClock(iso: string | null, tz: string | null): string {
  if (!iso) return '—';
  return formatSiteTime(iso, tz);
}

function captureLocation(): Promise<{ latitude: number | null; longitude: number | null; accuracy: number | null; state: LocationState }> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ latitude: null, longitude: null, accuracy: null, state: 'unavailable' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy, state: null }),
      (err) => resolve({ latitude: null, longitude: null, accuracy: null, state: err.code === err.PERMISSION_DENIED ? 'declined' : 'unavailable' }),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
    );
  });
}

export function WorkerClockPage() {
  const [lang, setLang] = useState<Lang>(() => {
    try { return (localStorage.getItem(LS_LANG) as Lang) || 'es'; } catch { return 'es'; }
  });
  const t = STR[lang];

  const [phone, setPhone] = useState(() => { try { return localStorage.getItem(LS_PHONE) ?? ''; } catch { return ''; } });
  const [pin, setPin] = useState(() => { try { return localStorage.getItem(LS_PIN) ?? ''; } catch { return ''; } });
  const [signedIn, setSignedIn] = useState(false);
  const [ctx, setCtx] = useState<WorkerContext | null>(null);
  // ⛔ WHERE THE WORKER IS, ANSWERED BY THE PHONE. There is no list of
  // buildings on this page any more, and there must never be one again.
  const [where, setWhere] = useState<WhereAmI | null>(null);
  const [locating, setLocating] = useState(false);
  // The building this punch will name. It arrives either from the phone (a
  // WhereFacility, carrying its distance) or from a tap on the scoped list (a
  // WhereOption, carrying none) — the punch only ever needs the id.
  const [chosen, setChosen] = useState<WhereOption | null>(null);
  const [noPlace, setNoPlace] = useState(false);
  const [optionFilter, setOptionFilter] = useState('');
  const [firstTime, setFirstTime] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAutoclose[]>([]);
  const [hours, setHours] = useState<WorkerHours | null>(null);
  const [showHours, setShowHours] = useState(false);
  // Night managers, Night + buildings (item #997). ⛔ Nothing here can block a
  // punch: every call is its own request and a failure only shows a sentence.
  const [visitOptions, setVisitOptions] = useState<VisitOption[] | null>(null);
  const [visitFilter, setVisitFilter] = useState('');
  const [tonight, setTonight] = useState<TonightSummary | null>(null);
  const nm = ctx?.night_manager ?? null;

  /**
   * ⛔ THE LANGUAGE IS A FACT ABOUT THE PERSON, NOT ABOUT THIS HANDSET.
   *
   * Pressed while signed in, the switch writes through to the worker's own
   * record, so a shared kiosk, a cleared browser or a second phone all open in
   * the language they chose. Before sign-in there is nobody to attach it to, so
   * the browser's own memory is all there is — and that is the only thing
   * localStorage is doing here now.
   *
   * The write is fire-and-forget on purpose: it must never make the switch feel
   * slow or, worse, refuse to flip. The screen is already in the new language.
   */
  function switchLang(l: Lang) {
    setLang(l);
    try { localStorage.setItem(LS_LANG, l); } catch { /* per-viewer convenience only */ }
    if (signedIn && phone && pin) {
      void workerClock('set_language', phone, pin, { language: l }).catch(() => {
        /* The preference did not stick for next time; this screen is still
           right, and the next sign-in will simply read the old value. */
      });
    }
  }

  const remember = useCallback((ph: string, pi: string) => {
    try { localStorage.setItem(LS_PHONE, ph); localStorage.setItem(LS_PIN, pi); } catch { /* optional */ }
  }, []);

  function forget() {
    try { localStorage.removeItem(LS_PHONE); localStorage.removeItem(LS_PIN); } catch { /* optional */ }
    setPhone(''); setPin(''); setSignedIn(false); setCtx(null); setHours(null); setPending([]);
  }

  const loadContext = useCallback(async (ph: string, pi: string) => {
    setBusy('context'); setRefusal(null);
    try {
      const r = await workerClock('context', ph, pi) as unknown as WorkerContext | { ok: false; message: string };
      if (!r.ok) { setRefusal((r as { message: string }).message); setSignedIn(false); return; }
      setCtx(r as WorkerContext);
      setSignedIn(true);
      // ⛔ The person's own preference wins over whatever this browser
      // remembered. This is the whole point: William reads English on a phone
      // that has never been set to English.
      const theirs = (r as WorkerContext).language;
      if (theirs === 'es' || theirs === 'en') {
        setLang(theirs);
        try { localStorage.setItem(LS_LANG, theirs); } catch { /* optional */ }
      }
      remember(ph, pi);
      // The one moment we say out loud that the phone and the code work.
      if (!(r as WorkerContext).has_ever_punched) setFirstTime(true);
      const nmc = (r as WorkerContext).night_manager ?? null;
      if (nmc) {
        // ⛔ A night manager is never asked where they are and never told they
        // are "not near a building" (Ben, 23 Oct and 27 Oct). Their punch
        // names no building; GPS still rides along for review only.
        setWhere(null); setChosen(null); setNoPlace(false);
        setVisitOptions(null);
        if (nmc.mode === 'night_and_buildings' && !(r as WorkerContext).on_clock) void loadTonight(ph, pi);
        else setTonight(null);
      } else {
        void locate(ph, pi);
      }
    } catch (e) {
      setRefusal(errorMessage(e, 'Something went wrong — try again.'));
    } finally {
      setBusy(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remember]);

  // Returning device: stored identity goes straight to the main screen.
  useEffect(() => {
    if (phone && pin && !signedIn && !busy) void loadContext(phone, pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ⛔ THE PHONE ANSWERS WHERE HE IS. He does not.
   *
   * Ben, 2026-09-22, relaying William: the old screen offered EVERY building —
   * 201 of them — because the table of worker assignments is empty, so the
   * "offer their assigned places" branch always found none and fell through to
   * its last resort. This replaces that question entirely.
   */
  const locate = useCallback(async (ph: string, pi: string) => {
    setLocating(true);
    setChosen(null);
    setNoPlace(false);
    // ⛔ D-03: a location problem never costs anybody a punch. Not knowing
    // where they are is a normal outcome, not an error to shout about — the
    // phone simply stops answering and the door hands back the SCOPED list.
    let coords: { latitude: number | null; longitude: number | null; accuracy: number | null } =
      { latitude: null, longitude: null, accuracy: null };
    try {
      const got = await captureLocation();
      coords = { latitude: got.latitude, longitude: got.longitude, accuracy: got.accuracy };
    } catch { /* location off, denied, or timed out — ask the door anyway */ }
    try {
      const r = await workerClock('where', ph, pi, {
        latitude: coords.latitude,
        longitude: coords.longitude,
        gps_accuracy_m: coords.accuracy,
      }) as unknown as (WhereAmI & { ok?: boolean });
      setWhere(r as WhereAmI);
      // One building at this spot is not a question. Two or three share an
      // address and only the person standing there knows which.
      if (r.found === true && r.here.length === 1) setChosen(r.here[0]);
      // One assigned building is not a question either — most people have
      // exactly one, and asking them to pick it is the scroll we deleted.
      if (r.found === false && r.reason !== 'nothing_nearby'
          && r.scope === 'assigned' && r.options.length === 1) {
        setChosen(r.options[0]);
      }
    } catch (e) {
      setRefusal(errorMessage(e, 'Something went wrong — try again.'));
    } finally {
      setLocating(false);
    }
  }, []);

  /** "Tonight — what your AM will see": the last night only, after its punch-out. */
  async function loadTonight(ph: string, pi: string) {
    try {
      const r = await workerClock('tonight', ph, pi) as { ok?: boolean; tonight?: TonightSummary | null };
      setTonight(r.ok ? (r.tonight ?? null) : null);
    } catch {
      setTonight(null); // a summary that will not load must never get in the way
    }
  }

  async function openVisitList() {
    setBusy('visit-list'); setRefusal(null); setVisitFilter('');
    try {
      const got = await captureLocation().catch(() => null);
      const r = await workerClock('visit', phone, pin, {
        what: 'options', latitude: got?.latitude ?? null, longitude: got?.longitude ?? null,
      }) as { ok?: boolean; message?: string; options?: VisitOption[] };
      if (r.ok === false) { setRefusal(r.message ?? 'Try again.'); return; }
      setVisitOptions(r.options ?? []);
    } catch (e) {
      setRefusal(errorMessage(e, 'Try again.'));
    } finally {
      setBusy(null);
    }
  }

  async function visit(what: 'check_in' | 'check_out', facilityId?: string) {
    setBusy(what); setRefusal(null); setNotice(null);
    try {
      const got = what === 'check_in' ? await captureLocation().catch(() => null) : null;
      const r = await workerClock('visit', phone, pin, {
        what, facility_id: facilityId ?? null,
        latitude: got?.latitude ?? null, longitude: got?.longitude ?? null, gps_accuracy_m: got?.accuracy ?? null,
      }) as { ok?: boolean; message?: string; visit?: { place_display: string } | null };
      if (r.ok === false) { setRefusal(r.message ?? 'Try again.'); return; }
      setVisitOptions(null);
      setCtx((c) => (c ? { ...c, visit: r.visit ?? null } : c));
      if (what === 'check_out') setNotice(t.checkedOut);
    } catch (e) {
      setRefusal(errorMessage(e, 'Try again.'));
    } finally {
      setBusy(null);
    }
  }

  /**
   * The screen is still waiting on an answer it asked for.
   *
   * ⛔ D-03 — this is NOT a punch being refused for location. "Not here"
   * clears it in one tap and the punch goes through with no building, and a
   * worker whose phone simply cannot see any building is never asked at all.
   */
  const askingWhere = !noPlace && !chosen && where !== null
    && !(where.found === false && where.reason === 'nothing_nearby');

  async function doPunch(eventType: 'in' | 'out') {
    // Punching OUT uses the building the open shift already names — never asks
    // again. Punching IN needs either a chosen building or an explicit "not
    // here", and the button is not offered until one of those is true.
    const facilityId = eventType === 'out'
      ? (ctx?.on_clock?.facility_id ?? null)
      : (chosen?.facility_id ?? null);
    setBusy(eventType); setNotice(null); setRefusal(null);
    if (eventType === 'in') setTonight(null);
    setVisitOptions(null);
    try {
      const coords = await captureLocation();
      const r = await workerClock('punch', phone, pin, {
        event_type: eventType,
        facility_id: facilityId,
        market_region: null,
        service_line_code: null,
        shift_code: null,
        latitude: coords.latitude,
        longitude: coords.longitude,
        gps_accuracy_m: coords.accuracy,
        location_state: coords.state,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : null,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }) as unknown as (PunchResult & { ok?: boolean; message?: string });
      if (r.ok === false) { setRefusal(r.message ?? 'Try again.'); return; }
      setNotice(`${t.recorded} — ${formatSiteTime(new Date().toISOString(), r.site_tz ?? null)}`);
      setPending(r.pending_autocloses ?? []);
      await loadContext(phone, pin);
    } catch (e) {
      setRefusal(errorMessage(e, 'Try again.'));
    } finally {
      setBusy(null);
    }
  }

  async function resolveItem(item: PendingAutoclose, resolution: AutocloseResolution, outAt: string, note: string): Promise<boolean> {
    setBusy('resolve'); setRefusal(null);
    try {
      const r = await resolveAutoclose(phone, pin, item, resolution, outAt, note);
      if (r.ok === false) { setRefusal(r.message ?? 'Try again.'); return false; }
      setPending((p) => p.filter((x) => x.exception_id !== item.exception_id));
      setNotice(resolution === 'forgot_out' ? t.savedOut
        : resolution === 'worked_full' ? t.sentHrFull
        : resolution === 'not_sure' ? t.sentHrNotSure : t.deferred);
      return true;
    } catch (e) {
      setRefusal(errorMessage(e, 'Try again.'));
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function loadHours() {
    setBusy('hours'); setRefusal(null);
    try {
      const r = await workerClock('hours', phone, pin) as unknown as WorkerHours | { ok: false; message: string };
      if (!r.ok) { setRefusal((r as { message: string }).message); return; }
      setHours(r as WorkerHours);
      setShowHours(true);
    } catch (e) {
      setRefusal(errorMessage(e, 'Try again.'));
    } finally {
      setBusy(null);
    }
  }

  /**
   * THE TIME CARD — one row per night, with the clock times, their own rate and
   * an estimate.
   *
   * ⛔ D-05 in the one place a worker reads it: a night with no VERIFIED rate
   * shows its HOURS and a sentence. Never a zero, never a blank. The server
   * sends `amount` as null rather than 0 precisely so this cannot be got wrong
   * by a formatter.
   *
   * ⛔ Their own hours only. There is no building total here, no colleague, and
   * no export button — if they want it on paper, their manager or HR prints it.
   */
  const periodCard = (label: string, period?: WorkerPeriod) => {
    if (!period) return null;
    const money = (n: number) => n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
    return (
      <div className="mt-3 rounded-2xl border border-tcs-border bg-tcs-raised p-4">
        <div className="flex items-baseline justify-between">
          <div className="text-sm font-bold text-tcs-text">{label}</div>
          <div className="text-xs text-tcs-text-muted">{fmtDate(period.start)} – {fmtDate(period.end)}</div>
        </div>

        <div className="mt-1 flex items-end justify-between gap-3">
          <div>
            <div className="text-2xl font-bold tabular-nums text-tcs-text">
              {period.hours_total.toFixed(2)} {t.hours}
            </div>
          </div>
          {period.amount_estimate != null && (
            <div className="text-right">
              <div className="text-[11px] text-tcs-text-muted">{t.estimated}</div>
              <div className="text-lg font-bold tabular-nums text-tcs-text">{money(period.amount_estimate)}</div>
              {period.rate_display != null && (
                <div className="text-[10.5px] text-tcs-text-muted">
                  {money(period.rate_display)} {t.perHour}
                </div>
              )}
            </div>
          )}
        </div>

        {period.rows.length === 0 ? (
          <div className="mt-2 text-sm text-tcs-text-muted">{t.noHours}</div>
        ) : (
          <table className="mt-3 w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-tcs-text-muted">
                <th className="py-1 font-semibold">{t.colDay}</th>
                <th className="py-1 font-semibold">{t.colIn}</th>
                <th className="py-1 font-semibold">{t.colOut}</th>
                <th className="py-1 text-right font-semibold">{t.colHours}</th>
              </tr>
            </thead>
            <tbody>
              {period.rows.map((r) => (
                <tr key={r.segment_id} className="border-t border-tcs-border">
                  <td className="py-1.5 text-tcs-text">
                    {fmtDate(r.shift_date)}
                    <div className="text-[10.5px] text-tcs-text-muted">{r.place_display}</div>
                  </td>
                  <td className="py-1.5 tabular-nums text-tcs-text-secondary">{fmtClock(r.in_at, r.site_tz)}</td>
                  <td className="py-1.5 tabular-nums text-tcs-text-secondary">
                    {r.out_at ? fmtClock(r.out_at, r.site_tz) : <span className="text-tcs-text-muted">{t.stillOpen}</span>}
                  </td>
                  <td className="py-1.5 text-right font-semibold tabular-nums text-tcs-text">{r.hours.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-tcs-border-strong">
                <td className="py-1.5 font-bold text-tcs-text" colSpan={3}>{t.total}</td>
                <td className="py-1.5 text-right font-bold tabular-nums text-tcs-text">
                  {period.hours_total.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {/* A flagged night, said in words they can act on. */}
        {period.rows.some((r) => r.open_exception) && (
          <div className="mt-2 rounded-xl border border-st-warn-border bg-st-warn-bg px-3 py-2 text-[12px] text-st-warn-fg">
            <b>{period.rows.filter((r) => r.open_exception).map((r) => fmtDate(r.shift_date)).join(' · ')}</b>
            {' — '}{t.needsAnswer}
          </div>
        )}

        {/* D-05, in their own language. */}
        {period.hours_total > 0 && period.amount_estimate == null && (
          <div className="mt-2 rounded-xl border border-st-info-border bg-st-info-bg px-3 py-2 text-[12px] text-st-info-fg">
            {t.noRateYet}
          </div>
        )}
        {period.amount_estimate != null && period.unrated_hours > 0 && (
          <div className="mt-2 rounded-xl border border-st-info-border bg-st-info-bg px-3 py-2 text-[12px] text-st-info-fg">
            {t.someHoursNoRate}
          </div>
        )}
        {period.amount_estimate != null && (
          <div className="mt-2 text-[10.5px] text-tcs-text-muted">{t.estimateNote}</div>
        )}
      </div>
    );
  };

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col px-4 py-6">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-bold uppercase tracking-wider text-tcs-text-muted">Terminal Cleaning Services</div>
        <div className="flex gap-1">
          {(['es', 'en'] as const).map((l) => (
            <button key={l} type="button" onClick={() => switchLang(l)}
              className={`rounded-md px-2 py-0.5 text-xs font-semibold ${lang === l ? 'bg-tcs-inverse text-tcs-inverse-ink' : 'text-tcs-text-secondary'}`}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {!signedIn ? (
        <div className="mt-6 rounded-2xl border border-tcs-border bg-tcs-raised p-5">
          <h1 className="text-lg font-bold text-tcs-text">{t.title}</h1>
          <label className="mt-4 block text-xs font-semibold text-tcs-text-secondary" htmlFor="clock-phone">{t.phone}</label>
          <input id="clock-phone" value={phone} inputMode="tel" autoComplete="tel"
            onChange={(e) => setPhone(e.target.value)}
            className="mt-1 w-full rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-3 text-lg text-tcs-text" />
          <label className="mt-3 block text-xs font-semibold text-tcs-text-secondary" htmlFor="clock-pin">{t.pin}</label>
          <input id="clock-pin" value={pin} inputMode="numeric" type="password" autoComplete="off"
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="mt-1 w-40 rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-3 text-center text-lg tracking-[0.4em] text-tcs-text" />
          <div className="mt-4">
            <Button loading={busy === 'context'} disabled={phone.trim() === '' || pin.length < 4}
              onClick={() => void loadContext(phone, pin)} className="w-full py-3">
              {t.signIn}
            </Button>
          </div>
          {refusal && <div className="mt-3 rounded-lg border border-st-danger-border bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg">{refusal}</div>}
        </div>
      ) : (
        <>
          <div className="mt-4 flex items-baseline justify-between">
            <h1 className="text-xl font-bold text-tcs-text">{t.hello}, {ctx?.first_name}</h1>
            <button type="button" onClick={forget} className="text-xs text-tcs-text-muted underline">{t.notYou}</button>
          </div>

          {ctx?.on_clock && (
            <div className="mt-2 rounded-xl border border-st-ok-border bg-st-ok-bg px-3 py-2 text-sm text-st-ok-fg">
              {t.onClockSince} {fmtWhenLocal(ctx.on_clock.since, ctx.on_clock.site_tz)}
              {/* A night manager's night names no building — say nothing rather than "No building named". */}
              {!(nm && !ctx.on_clock.facility_id) && <> {t.at} {ctx.on_clock.place_display}</>}
            </div>
          )}

          {/* ⛔ THE TEST IS SIGNING IN, and it always was — this only says so.
              Shown once, to somebody who has never punched, and never again.
              It records nothing: there is no test punch to clean up after. */}
          {firstTime && !ctx?.on_clock && (
            <div className="mt-3 rounded-2xl border border-st-ok-border bg-st-ok-bg px-4 py-4 text-st-ok-fg">
              <div className="text-base font-bold">{t.firstTimeTitle}</div>
              <div className="mt-1 text-[12.5px]">{t.firstTimeBody}</div>
              <Button size="sm" variant="secondary" className="mt-3" onClick={() => setFirstTime(false)}>
                {t.firstTimeOk}
              </Button>
            </div>
          )}

          {/* ⛔ NO LIST OF BUILDINGS. EVER. The phone says where he is; he
              confirms only when two genuinely share an address. */}
          {ctx?.on_clock ? (
            /* Already on the clock: there is nothing to ask. One button —
               plus, for a night manager in Night + buildings, the building
               they are in (its name only) and the check-in controls. */
            <div className="mt-5">
              {nm?.mode === 'night_and_buildings' && (
                <div className="mb-4">
                  {ctx.visit ? (
                    <div className="rounded-2xl border border-st-ok-border bg-st-ok-bg px-4 py-3 text-center text-st-ok-fg">
                      <div className="text-[11px] font-semibold uppercase tracking-wider">{t.inBuilding}</div>
                      <div className="mt-0.5 text-lg font-bold leading-tight">{ctx.visit.place_display}</div>
                    </div>
                  ) : null}
                  {visitOptions === null ? (
                    <div className="mt-2 flex flex-col gap-2">
                      {ctx.visit ? (
                        <>
                          <Button loading={busy === 'check_out'} disabled={busy !== null}
                            onClick={() => void visit('check_out')} className="w-full py-4 text-base">
                            {t.checkOutBuilding}
                          </Button>
                          <Button variant="secondary" loading={busy === 'visit-list'} disabled={busy !== null}
                            onClick={() => void openVisitList()} className="w-full py-3 text-base">
                            {t.checkInElsewhere}
                          </Button>
                          <div className="text-center text-[11px] text-tcs-text-muted">{t.elsewhereNote}</div>
                        </>
                      ) : (
                        <Button loading={busy === 'visit-list'} disabled={busy !== null}
                          onClick={() => void openVisitList()} className="w-full py-4 text-base">
                          {t.checkInBuilding}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="mt-2">
                      <div className="text-sm font-semibold text-tcs-text">{t.whichBuilding}</div>
                      <div className="text-[11.5px] text-tcs-text-muted">{t.yourMarket}{nm.market ? ` · ${nm.market}` : ''}</div>
                      {visitOptions.length > 12 && (
                        <input value={visitFilter} onChange={(e) => setVisitFilter(e.target.value)}
                          placeholder={t.findBuilding} aria-label={t.findBuilding}
                          className="mt-2 w-full rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-3 text-base text-tcs-text" />
                      )}
                      <div className="mt-2 flex flex-col gap-2">
                        {visitOptions
                          .filter((o) => visitFilter.trim() === ''
                            || o.facility_name.toLowerCase().includes(visitFilter.trim().toLowerCase()))
                          .slice(0, 12)
                          .map((o, i) => (
                            <button key={o.facility_id} type="button" disabled={busy !== null}
                              onClick={() => void visit('check_in', o.facility_id)}
                              className="rounded-2xl border border-tcs-border-strong bg-tcs-raised px-4 py-3 text-left text-base font-semibold text-tcs-text">
                              {i === 0 && visitFilter.trim() === '' && o.distance_m != null && (
                                <span className="mb-0.5 block text-[10.5px] font-bold uppercase tracking-wider text-st-ok-fg">{t.closest}</span>
                              )}
                              {o.display}
                              {o.distance_m != null && (
                                <span className="block text-[11px] font-normal text-tcs-text-muted">{fmtAway(o.distance_m)}</span>
                              )}
                            </button>
                          ))}
                      </div>
                      <button type="button" onClick={() => setVisitOptions(null)}
                        className="mt-2 w-full text-center text-xs text-tcs-text-muted underline">
                        {t.cancel}
                      </button>
                    </div>
                  )}
                </div>
              )}
              <Button variant="secondary" loading={busy === 'out'} disabled={busy !== null}
                onClick={() => void doPunch('out')} className="w-full py-5 text-lg">
                {nm ? t.nmPunchOut : t.punchOut}
              </Button>
            </div>
          ) : nm ? (
            /* A night manager, off the clock: one button, no building asked,
               no "not near a building" (Ben, 23 Oct; item #997). */
            <div className="mt-4">
              <div className="text-center text-sm text-tcs-text-secondary">{t.nmPunchInHint}</div>
              <div className="mt-3">
                <Button loading={busy === 'in'} disabled={busy !== null}
                  onClick={() => void doPunch('in')} className="w-full py-5 text-lg">
                  {t.punchIn}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-4">
                {locating && (
                  <div className="rounded-2xl border border-tcs-border bg-tcs-raised px-4 py-5 text-center text-sm text-tcs-text-secondary">
                    {t.locating}
                  </div>
                )}

                {!locating && where?.found === true && where.here.length === 1 && (
                  <div className="rounded-2xl border border-st-ok-border bg-st-ok-bg px-4 py-4 text-center">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-st-ok-fg">{t.youAreAt}</div>
                    <div className="mt-1 text-lg font-bold leading-tight text-st-ok-fg">
                      {where.here[0].facility_name}
                    </div>
                  </div>
                )}

                {/* Two or three buildings at one address — the only question
                    this page asks, and it is one tap with no scrolling. */}
                {!locating && where?.found === true && where.here.length > 1 && (
                  <div>
                    <div className="text-sm font-semibold text-tcs-text">{t.whichOne}</div>
                    <div className="mt-2 flex flex-col gap-2">
                      {where.here.map((f) => (
                        <button key={f.facility_id} type="button"
                          onClick={() => { setChosen(f); setNoPlace(false); }}
                          className={`rounded-2xl border px-4 py-4 text-left text-base font-semibold ${
                            chosen?.facility_id === f.facility_id
                              ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
                              : 'border-tcs-border-strong bg-tcs-raised text-tcs-text'}`}>
                          {f.facility_name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Location is ON and they are simply not at work. Say where
                    they appear to be — in the only vocabulary this system has,
                    the nearest building we know — and then ask. */}
                {!locating && where?.found === false && where.reason === 'nothing_nearby' && (
                  <div className="rounded-2xl border border-st-warn-border bg-st-warn-bg px-4 py-4 text-center text-st-warn-fg">
                    <div className="text-sm font-bold">{t.notNearAny}</div>
                    {where.nearest && (
                      <div className="mt-1 text-[12.5px]">
                        {t.itShowsYou} <b>{fmtAway(where.nearest.distance_m)}</b> {t.fromA}{' '}
                        <b>{where.nearest.facility_name}</b>.
                      </div>
                    )}
                    <div className="mt-2 text-[12.5px] font-semibold">{t.punchAnyway}</div>
                  </div>
                )}

                {/* Location is OFF, or too rough to tell two buildings apart.
                    ⛔ The list is SCOPED — a night manager's own market, or the
                    buildings they work — and the screen says when it is not. */}
                {!locating && where?.found === false && where.reason !== 'nothing_nearby' && !noPlace && (
                  <div>
                    <div className="rounded-2xl border border-st-info-border bg-st-info-bg px-4 py-3 text-st-info-fg">
                      <div className="text-sm font-bold">{t.noPlace}</div>
                      <div className="mt-1 text-[12.5px]">
                        {t.whereAreYou}
                        {where.scope === 'market' && where.scope_label && <> · {where.scope_label}</>}
                      </div>
                      {where.scope === 'all' && (
                        <div className="mt-1 text-[11.5px]">{t.scopeAll}</div>
                      )}
                    </div>
                    {where.options.length > 12 && (
                      <input value={optionFilter} onChange={(e) => setOptionFilter(e.target.value)}
                        placeholder={t.findBuilding} aria-label={t.findBuilding}
                        className="mt-2 w-full rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-3 text-base text-tcs-text" />
                    )}
                    <div className="mt-2 flex flex-col gap-2">
                      {where.options
                        .filter((o) => optionFilter.trim() === ''
                          || o.facility_name.toLowerCase().includes(optionFilter.trim().toLowerCase()))
                        .slice(0, 12)
                        .map((o) => (
                          <button key={o.facility_id} type="button"
                            onClick={() => { setChosen(o); setNoPlace(false); }}
                            className={`rounded-2xl border px-4 py-4 text-left text-base font-semibold ${
                              chosen?.facility_id === o.facility_id
                                ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
                                : 'border-tcs-border-strong bg-tcs-raised text-tcs-text'}`}>
                            {o.facility_name}
                          </button>
                        ))}
                    </div>
                  </div>
                )}

                {!locating && noPlace && (
                  <div className="rounded-2xl border border-st-info-border bg-st-info-bg px-4 py-4 text-center text-st-info-fg">
                    <div className="text-sm font-bold">{t.noPlace}</div>
                    <div className="mt-1 text-[12.5px]">{t.noPlaceBody}</div>
                  </div>
                )}
              </div>

              <div className="mt-4">
                <Button loading={busy === 'in'}
                  disabled={busy !== null || locating || askingWhere}
                  onClick={() => void doPunch('in')} className="w-full py-5 text-lg">
                  {t.punchIn}
                </Button>
              </div>

              {/* The way out of a wrong guess, without ever offering a list.
                  It is offered wherever the screen asked a question at all —
                  the phone's guess, and the scoped list when it could not. */}
              {!locating && where !== null && !noPlace
                && !(where.found === false && where.reason === 'nothing_nearby') && (
                <button type="button" onClick={() => { setNoPlace(true); setChosen(null); }}
                  className="mt-2 w-full text-center text-xs text-tcs-text-muted underline">
                  {t.notHere}
                </button>
              )}
            </>
          )}
          <div className="mt-2 text-[11px] text-tcs-text-muted">{t.recordedAlways}</div>

          {notice && <div className="mt-3 rounded-xl border border-st-ok-border bg-st-ok-bg px-3 py-2 text-sm text-st-ok-fg">{notice}</div>}
          {refusal && <div className="mt-3 rounded-xl border border-st-danger-border bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg">{refusal}</div>}

          {/* "Tonight — what your AM will see." Night + buildings only, after
              the punch-out, the last night only; gone at the next punch-in.
              Read-only, no history (Ben, 2026-10-27). */}
          {nm?.mode === 'night_and_buildings' && !ctx?.on_clock && tonight && (
            <TonightCard t={t} tonight={tonight} />
          )}

          {/* The next-punch prompt — no ✕; it leaves when answered or deferred. */}
          <AutoclosePrompt t={t} pending={pending} busy={busy} onResolve={resolveItem} />

          {/* My hours — their own time card. */}
          <div className="mt-5">
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" loading={busy === 'hours'} onClick={() => void loadHours()}>
                {t.myHours}
              </Button>
              {showHours && hours && (
                <button type="button" onClick={() => void loadHours()} className="text-xs text-tcs-text-muted underline">{t.refresh}</button>
              )}
            </div>
            {/* Ben, 2026-09-22: the card no longer waits for anybody to confirm
                this browser. The same phone and PIN that authorize a punch —
                which CREATES a shift — now also read one. */}
            {showHours && hours && (
              <>
                {periodCard(t.thisPeriod, hours.current_period)}
                {periodCard(t.lastPeriod, hours.previous_period)}
              </>
            )}
          </div>
        </>
      )}

      <footer className="mt-auto pt-8 text-center text-xs text-tcs-text-muted">
        {HR_HELP_LINE ? `${t.help}: ${HR_HELP_LINE}` : t.helpNoNumber}
      </footer>
    </div>
  );
}

/** Whole minutes as "1 h 12 m", so the lines add up exactly to the hours worked. */
function hrs(m: number): string {
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} m`;
}

/**
 * "Tonight — what your AM will see": each building with its check-in and
 * check-out times and hours, the driving / between-buildings time, and the
 * hours worked (punch in to punch out). It adds up to the hours worked.
 */
function TonightCard({ t, tonight }: { t: Record<string, string>; tonight: TonightSummary }) {
  return (
    <div className="mt-4 rounded-2xl border border-tcs-border bg-tcs-raised p-4">
      <div className="text-sm font-bold text-tcs-text">{t.tonightTitle}</div>
      <div className="text-[11px] text-tcs-text-muted">{fmtDate(tonight.shift_date)}</div>
      {tonight.visits.length === 0 && (
        <div className="mt-2 text-[12.5px] text-tcs-text-secondary">{t.noCheckins}</div>
      )}
      <table className="mt-2 w-full border-collapse text-[12.5px]">
        <tbody>
          {tonight.visits.map((v, i) => (
            <tr key={i} className="border-t border-tcs-border">
              <td className="py-1.5 pr-2 text-tcs-text">{v.place_display}</td>
              <td className="whitespace-nowrap py-1.5 pr-2 tabular-nums text-tcs-text-secondary">
                {formatSiteTime(v.in_at, tonight.site_tz)}–{formatSiteTime(v.out_at, tonight.site_tz)}
              </td>
              <td className="whitespace-nowrap py-1.5 text-right font-semibold tabular-nums text-tcs-text">{hrs(v.minutes)}</td>
            </tr>
          ))}
          <tr className="border-t border-tcs-border">
            <td className="py-1.5 pr-2 italic text-tcs-text-secondary" colSpan={2}>{t.driving}</td>
            <td className="whitespace-nowrap py-1.5 text-right font-semibold tabular-nums text-tcs-text">{hrs(tonight.between_minutes)}</td>
          </tr>
          <tr className="border-t-2 border-tcs-border-strong">
            <td className="py-1.5 font-bold text-tcs-text">{t.hoursWorked}</td>
            <td className="whitespace-nowrap py-1.5 pr-2 tabular-nums text-tcs-text-secondary">
              {formatSiteTime(tonight.started_at, tonight.site_tz)}–{formatSiteTime(tonight.ended_at, tonight.site_tz)}
            </td>
            <td className="whitespace-nowrap py-1.5 text-right font-bold tabular-nums text-tcs-text">{hrs(tonight.worked_minutes)}</td>
          </tr>
        </tbody>
      </table>
      <div className="mt-2 text-[11px] text-tcs-text-muted">{t.tonightNote}</div>
    </div>
  );
}

export type AutocloseResolution = 'forgot_out' | 'worked_full' | 'not_sure' | 'defer';

/** Answer an auto-closed night through the worker door — /clock and /clock/kiosk alike. */
export async function resolveAutoclose(
  phone: string, pin: string, item: PendingAutoclose, resolution: AutocloseResolution, outAt: string, note: string,
): Promise<{ ok?: boolean; message?: string }> {
  const payload: Record<string, unknown> = { exception_id: item.exception_id, resolution };
  if (resolution === 'forgot_out') payload.punch_out_at = siteInputToISO(outAt, item.site_tz);
  if (note.trim()) payload.note = note.trim();
  return await workerClock('resolve', phone, pin, payload) as { ok?: boolean; message?: string };
}

/**
 * The next-punch question about a night that was closed automatically — the
 * same card, words and choices on the worker's phone and on a building's time
 * clock. No ✕: it leaves when answered or put off.
 */
export function AutoclosePrompt({ t, pending, busy, onResolve }: {
  t: Record<string, string>;
  pending: PendingAutoclose[];
  busy: string | null;
  onResolve: (item: PendingAutoclose, resolution: AutocloseResolution, outAt: string, note: string) => Promise<boolean>;
}) {
  // One active item at a time.
  const [activeExc, setActiveExc] = useState<string | null>(null);
  const [mode, setMode] = useState<'forgot_out' | 'worked_full' | 'not_sure' | null>(null);
  const [outAt, setOutAt] = useState('');
  const [note, setNote] = useState('');

  async function answer(item: PendingAutoclose, resolution: AutocloseResolution) {
    const done = await onResolve(item, resolution, outAt, note);
    if (done) { setActiveExc(null); setMode(null); setNote(''); }
  }

  if (pending.length === 0) return null;
  return (
    <div className="mt-3 rounded-xl border border-st-warn-border bg-st-warn-bg p-3">
      <div className="text-sm font-bold text-st-warn-fg">{t.promptTitle}</div>
      {pending.map((item) => {
        const isActive = activeExc === item.exception_id;
        return (
          <div key={item.exception_id} className="mt-2 rounded-lg border border-tcs-border bg-tcs-raised p-3">
            <div className="text-sm text-tcs-text">
              {t.promptBody1} <b>{fmtWhenLocal(item.started_at, item.site_tz)}</b> — {item.place_display} — {t.promptBody2} <b>{fmtWhenLocal(item.auto_closed_at, item.site_tz)}</b>.
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {(['forgot_out', 'worked_full', 'not_sure'] as const).map((m) => (
                <button key={m} type="button" disabled={busy !== null}
                  onClick={() => { setActiveExc(item.exception_id); setMode(m); setOutAt(isoToSiteInput(item.auto_closed_at, item.site_tz)); setNote(''); }}
                  className={`rounded-lg px-3 py-2 text-xs font-semibold ${isActive && mode === m
                    ? 'bg-tcs-inverse text-tcs-inverse-ink'
                    : 'border border-tcs-border-strong bg-tcs-bg text-tcs-text'}`}>
                  {m === 'forgot_out' ? t.forgotOut : m === 'worked_full' ? t.workedFull : t.notSure}
                </button>
              ))}
              <button type="button" disabled={busy !== null} onClick={() => void answer(item, 'defer')}
                className="rounded-lg px-3 py-2 text-xs font-medium text-tcs-text-secondary">
                {t.later}
              </button>
            </div>
            {isActive && mode === 'forgot_out' && (
              <div className="mt-3">
                <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor={`out-${item.exception_id}`}>{t.whenLeave} ({tzLabel(item.site_tz)})</label>
                <input id={`out-${item.exception_id}`} type="datetime-local" value={outAt}
                  min={isoToSiteInput(item.started_at, item.site_tz)} max={isoToSiteInput(item.auto_closed_at, item.site_tz)}
                  onChange={(e) => setOutAt(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-2 text-sm text-tcs-text" />
                <Button size="sm" className="mt-2" loading={busy === 'resolve'} onClick={() => void answer(item, 'forgot_out')}>
                  {t.saveOut}
                </Button>
              </div>
            )}
            {isActive && (mode === 'worked_full' || mode === 'not_sure') && (
              <div className="mt-3">
                <textarea value={note} rows={2} onChange={(e) => setNote(e.target.value)}
                  placeholder={mode === 'worked_full' ? t.noteOptional : t.noteRequired}
                  className="w-full rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-2 text-sm text-tcs-text" />
                <Button size="sm" className="mt-2" loading={busy === 'resolve'}
                  disabled={mode === 'not_sure' && note.trim() === ''}
                  onClick={() => void answer(item, mode)}>
                  {t.sendHr}
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
